/**
 * AmbientEffectsContext - Global ambient effects settings management
 *
 * Provides centralized control for ambient visual effects:
 * - Enable/disable all effects
 * - Individual effect toggles
 * - Persists settings to localStorage
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AmbientEffects from '../components/effects/AmbientEffects';

const AmbientEffectsContext = createContext();

const STORAGE_KEY = 'ambientEffectsSettings';

// Default settings
const DEFAULT_SETTINGS = {
  enabled: true,
  dustParticles: true,
  dustCount: 25,
  scanLines: true,
  scanLineOpacity: 0.03,
  movingScanLine: true,
  screenFlicker: true,
  flickerIntensity: 0.02,
  vignette: true,
  vignetteIntensity: 0.3,
  noise: false,
  noiseOpacity: 0.015,
  glowPulse: true,
  glowColor: 'rgba(52, 152, 219, 0.08)',
};

export const useAmbientEffects = () => {
  const context = useContext(AmbientEffectsContext);
  if (!context) {
    throw new Error('useAmbientEffects must be used within an AmbientEffectsProvider');
  }
  return context;
};

export const AmbientEffectsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings((prev) => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      console.warn('Failed to load ambient effects settings:', e);
    }
    setIsLoaded(true);
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      } catch (e) {
        console.warn('Failed to save ambient effects settings:', e);
      }
    }
  }, [settings, isLoaded]);

  /**
   * Toggle all effects on/off
   */
  const toggleEffects = useCallback(() => {
    setSettings((prev) => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  /**
   * Enable all effects
   */
  const enableEffects = useCallback(() => {
    setSettings((prev) => ({ ...prev, enabled: true }));
  }, []);

  /**
   * Disable all effects
   */
  const disableEffects = useCallback(() => {
    setSettings((prev) => ({ ...prev, enabled: false }));
  }, []);

  /**
   * Update a specific setting
   */
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Update multiple settings at once
   */
  const updateSettings = useCallback((updates) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Reset all settings to defaults
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  /**
   * Set intensity preset
   */
  const setIntensityPreset = useCallback((preset) => {
    const presets = {
      minimal: {
        dustParticles: true,
        dustCount: 10,
        scanLines: false,
        movingScanLine: false,
        screenFlicker: false,
        vignette: true,
        vignetteIntensity: 0.2,
        noise: false,
        glowPulse: false,
      },
      balanced: {
        dustParticles: true,
        dustCount: 25,
        scanLines: true,
        scanLineOpacity: 0.03,
        movingScanLine: true,
        screenFlicker: true,
        flickerIntensity: 0.02,
        vignette: true,
        vignetteIntensity: 0.3,
        noise: false,
        glowPulse: true,
      },
      immersive: {
        dustParticles: true,
        dustCount: 40,
        scanLines: true,
        scanLineOpacity: 0.05,
        movingScanLine: true,
        screenFlicker: true,
        flickerIntensity: 0.03,
        vignette: true,
        vignetteIntensity: 0.4,
        noise: true,
        noiseOpacity: 0.02,
        glowPulse: true,
      },
    };

    if (presets[preset]) {
      setSettings((prev) => ({ ...prev, ...presets[preset] }));
    }
  }, []);

  return (
    <AmbientEffectsContext.Provider
      value={{
        settings,
        isEnabled: settings.enabled,
        toggleEffects,
        enableEffects,
        disableEffects,
        updateSetting,
        updateSettings,
        resetSettings,
        setIntensityPreset,
      }}
    >
      {children}

      {/* Render ambient effects globally */}
      {isLoaded && <AmbientEffects {...settings} />}
    </AmbientEffectsContext.Provider>
  );
};

export default AmbientEffectsContext;
